import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { fetchReports } from "../../redux/slices/reports";
import "../../assets/scss/main-style.scss";
import { extractVinnyFromReportHtml, VinnyPrompt } from "./vinny-prompt";
import { useAnalytics } from "../../hooks/useAnalytics";

const VHR_viewFullReports = (item) => {
  const [reportHtml, setReportHtml] = useState("");
  const [vinnyData, setVinnyData] = useState({
    vinnyHtml: null,
    vinnyScripts: [],
    vinnyStylesheets: [],
  });
  const { viewFullReport } = useSelector((state) => state.report);
  const params = useParams();
  const { vin, isOwner } = params;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const iframeRef = useRef(null);
  const { trackEvent } = useAnalytics();
  // Holds { make, year } for the currently viewed vehicle so rule helpers
  // inside iframe callbacks can access it without closure staleness.

  const handleGoDashboard = () => {
    localStorage.removeItem("vhr.vehicle");
    navigate("/vehiclehistory/dashboard");
  };

  // Extract Vinny and set report HTML for iframe rendering
  useEffect(() => {
    if (viewFullReport && viewFullReport[0]?.html) {
      const reportHtmlContent = viewFullReport[0].html;

      const { vinnyHtml, vinnyScripts, vinnyStylesheets, modifiedReportHtml } =
        extractVinnyFromReportHtml(reportHtmlContent);
      if (vinnyHtml) {
        setReportHtml(modifiedReportHtml);
        setVinnyData({ vinnyHtml, vinnyScripts, vinnyStylesheets });
      } else {
        setReportHtml(reportHtmlContent);
      }
    }
  }, [viewFullReport]);

  const pushIframeAnalyticsEvent = useCallback(
    (eventData) => {
      const { event: eventName, ...eventParams } = eventData || {};
      if (!eventName) return;

      // Primary path: use centralized analytics hook/service.
      trackEvent(eventName, eventParams);

      // Safety path: always push to dataLayer + direct gtag so events are not
      // dropped during delayed analytics initialization.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        ...eventParams,
      });

      if (window.gtag && process.env.REACT_APP_GA_MEASUREMENT_ID) {
        window.gtag("event", eventName, eventParams);
      }
    },
    [trackEvent],
  );

  const applyIframeModalFix = useCallback(
    (iframe) => {
      try {
        const iframeDoc = iframe.contentDocument;
        const iframeWin = iframe.contentWindow;
        if (!iframeDoc || !iframeWin) return;

        // --- Make an open modal cover the whole app viewport -----------------
        // The report renders in an iframe sized to its full content height, so
        // the PARENT page scrolls and a `position: fixed` modal inside the iframe
        // is confined to the iframe's box (it can't cover the header/buttons and
        // drifts as the parent scrolls). When a modal opens we instead turn the
        // IFRAME itself into a full-viewport fixed overlay: the modal and its
        // backdrop then cover the entire app and the modal scrolls internally.
        const OVERLAY_IFRAME_PROPS = [
          ["position", "fixed"],
          ["top", "0"],
          ["left", "0"],
          ["right", "0"],
          ["bottom", "0"],
          ["width", "100%"],
          ["height", "100vh"],
          ["z-index", "2000"],
        ];

        const startViewportPin = (modal) => {
          if (!modal) return;

          // Remember the iframe's original inline styles so we can restore them.
          if (iframe.__savedInlineStyle == null) {
            iframe.__savedInlineStyle = iframe.getAttribute("style") || "";
          }
          // Guard against the auto-resize observer fighting the overlay height.
          iframe.__modalOverlayActive = true;

          OVERLAY_IFRAME_PROPS.forEach(([prop, value]) =>
            iframe.style.setProperty(prop, value, "important"),
          );

          // Ensure the modal fills the (now full-viewport) iframe and scrolls
          // internally instead of clipping tall content.
          modal.style.setProperty("position", "fixed", "important");
          modal.style.setProperty("inset", "0", "important");
          modal.style.setProperty("height", "100vh", "important");
          modal.style.setProperty("max-height", "100vh", "important");
          modal.style.setProperty("overflow-x", "hidden", "important");
          modal.style.setProperty("overflow-y", "auto", "important");

          // Lock parent page scroll while the overlay is active.
          document.documentElement.style.overflow = "hidden";
          document.body.style.overflow = "hidden";
        };

        const stopViewportPin = (modal) => {
          iframe.__modalOverlayActive = false;

          // Restore the iframe's original inline styles (auto-resize will resume).
          if (iframe.__savedInlineStyle != null) {
            iframe.setAttribute("style", iframe.__savedInlineStyle);
            iframe.__savedInlineStyle = null;
          } else {
            OVERLAY_IFRAME_PROPS.forEach(([prop]) =>
              iframe.style.removeProperty(prop),
            );
          }

          // Unlock parent scroll.
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";

          if (!modal) return;
          [
            "position",
            "inset",
            "height",
            "max-height",
            "overflow-x",
            "overflow-y",
          ].forEach((prop) => modal.style.removeProperty(prop));
        };

        const trackCustomStickerModalIfPresent = () => {
          const stickerModal = iframeDoc.getElementById("stickerModal");
          if (!stickerModal) return;

          let lastVisible = getComputedStyle(stickerModal).display !== "none";

          const emitOpen = () => {
            const interaction = iframeWin.__lastWindowStickerInteraction;
            pushIframeAnalyticsEvent({
              event: "window_sticker_modal_open",
              page_location: window.location.href,
              page_path: window.location.pathname,
              click_element_id: interaction?.clickElementId || "",
              interaction_type: "window_sticker_modal",
              modal_id: stickerModal.getAttribute("id") || "stickerModal",
              modal_target: interaction?.modalTarget || "#stickerModal",
              data_url: interaction?.dataUrl || "",
              analytics_source: "vhr_view_full_report",
            });
          };

          const emitClose = () => {
            const interaction = iframeWin.__lastWindowStickerInteraction;
            pushIframeAnalyticsEvent({
              event: "window_sticker_modal_close",
              page_location: window.location.href,
              page_path: window.location.pathname,
              click_element_id: interaction?.clickElementId || "",
              interaction_type: "window_sticker_modal",
              modal_id: stickerModal.getAttribute("id") || "stickerModal",
              modal_target: interaction?.modalTarget || "#stickerModal",
              data_url: interaction?.dataUrl || "",
              analytics_source: "vhr_view_full_report",
            });
          };

          const observer = new MutationObserver(() => {
            const visible = getComputedStyle(stickerModal).display !== "none";
            if (visible === lastVisible) return;
            lastVisible = visible;
            if (visible) {
              // Keep the custom sticker modal pinned to the viewport on scroll.
              startViewportPin(stickerModal);
              emitOpen();
            } else {
              stopViewportPin(stickerModal);
              emitClose();
            }
          });

          observer.observe(stickerModal, {
            attributes: true,
            attributeFilter: ["style", "class"],
          });

          iframe.__stickerModalObserver = observer;
        };

        trackCustomStickerModalIfPresent();

        // Restyle the report-service #stickerModal to match the auto-member-ui
        // look & feel. Visual-only rules (dimensions, borders, fonts, spacing) —
        // deliberately does NOT touch modal-body overflow so scrolling keeps
        // working as-is.
        const injectStickerModalStyles = () => {
          if (iframeDoc.getElementById("consumer-sticker-modal-style")) return;
          const style = iframeDoc.createElement("style");
          style.id = "consumer-sticker-modal-style";
          style.textContent = `
          #stickerModal .modal-dialog {
            max-width: 1000px !important;
            width: 90% !important;
            margin: 2rem auto !important;
          }
          #stickerModal .modal-content {
            max-height: 85vh !important;
            overflow: hidden !important;
            border: none !important;
            border-radius: 6px !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25) !important;
          }
          #stickerModal .modal-header {
            border-bottom: none !important;
            padding: 16px 20px !important;
            align-items: center !important;
          }
          #stickerModal .modal-title {
            margin: 0 !important;
            width: 100% !important;
            font-size: 28px !important;
            font-weight: bold !important;
            line-height: 1.2 !important;
            text-align: center !important;
          }
          #stickerModal .modal-header .close,
          #stickerModal .modal-header .btn-close {
            font-size: 1.25rem !important;
            font-weight: bold !important;
            opacity: 0.75 !important;
          }
          #stickerModal .modal-body {
            padding: 40px 20px !important;
            min-height: 300px !important;
          }
          #stickerModal .modal-footer {
            padding: 20px 15px !important;
            margin-top: 8px !important;
            border-top: 1px solid #dee2e6 !important;
            justify-content: flex-start !important;
          }
          #stickerModal .modal-footer .btn,
          #stickerModal #downloadWindowStickerBtn,
          #stickerModal #downloadBtn {
            padding: 8px 20px !important;
            font-size: 14px !important;
          }
        `;
          (iframeDoc.head || iframeDoc.documentElement).appendChild(style);
        };
        injectStickerModalStyles();

        // Only allow the modal to be dismissed via its close (X) button:
        // disable backdrop-click and Escape-key closing.
        const enforceExplicitCloseOnly = () => {
          iframeDoc.querySelectorAll(".modal").forEach((m) => {
            m.setAttribute("data-bs-backdrop", "static");
            m.setAttribute("data-bs-keyboard", "false");
          });
        };
        enforceExplicitCloseOnly();

        // Custom (non-Bootstrap) sticker modal: block backdrop clicks + Escape so
        // it too only closes via its explicit close control.
        const stickerBackdropGuard = (e) => {
          const stickerModal = iframeDoc.getElementById("stickerModal");
          if (!stickerModal) return;
          if (getComputedStyle(stickerModal).display === "none") return;
          // A click directly on the overlay element is a backdrop click.
          if (e.target === stickerModal) {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        const stickerEscapeGuard = (e) => {
          if (e.key !== "Escape" && e.keyCode !== 27) return;
          const stickerModal = iframeDoc.getElementById("stickerModal");
          const stickerOpen =
            stickerModal && getComputedStyle(stickerModal).display !== "none";
          const bsOpen = iframeDoc.querySelector(".modal.show");
          if (stickerOpen || bsOpen) {
            e.preventDefault();
            e.stopPropagation();
          }
        };
        iframeDoc.addEventListener("click", stickerBackdropGuard, true);
        iframeDoc.addEventListener("keydown", stickerEscapeGuard, true);

        const $ = iframeWin.$ || iframeWin.jQuery;
        if ($) {
          $(iframeDoc).on("show.bs.modal", ".modal", function () {
            const modal = this;
            // Force close-only-via-X: disable backdrop + keyboard dismissal.
            modal.setAttribute("data-bs-backdrop", "static");
            modal.setAttribute("data-bs-keyboard", "false");
            try {
              const BS = iframeWin.bootstrap;
              const inst = BS?.Modal?.getInstance?.(modal);
              if (inst && inst._config) {
                inst._config.backdrop = "static";
                inst._config.keyboard = false;
              }
            } catch (e) {
              // no-op
            }
            // Pin the modal to the visible viewport and keep it there on scroll.
            startViewportPin(modal);

            const interaction = iframeWin.__lastWindowStickerInteraction;
            const modalId = modal.getAttribute("id") || "";
            const modalTarget = interaction?.modalTarget || "";
            const targetMatches =
              modalTarget &&
              (modalTarget === modalId || modalTarget === `#${modalId}`);
            const modalText = (modal.textContent || "").toLowerCase();
            const isRecentInteraction =
              interaction && Date.now() - interaction.timestamp < 3000;
            const looksLikeStickerModal =
              targetMatches ||
              (isRecentInteraction && modalText.includes("window sticker"));

            if (looksLikeStickerModal) {
              modal.dataset.windowStickerTracked = "true";
              pushIframeAnalyticsEvent({
                event: "window_sticker_modal_open",
                page_location: window.location.href,
                page_path: window.location.pathname,
                click_element_id: interaction?.clickElementId || "",
                interaction_type: "window_sticker_modal",
                modal_id: modalId,
                modal_target: modalTarget,
                data_url: interaction?.dataUrl || "",
                analytics_source: "vhr_view_full_report",
              });
            }
          });

          // Ensure the backdrop covers the full viewport (the iframe is now a
          // full-viewport fixed overlay while the modal is open).
          $(iframeDoc).on("shown.bs.modal", ".modal", function () {
            const backdrop = $(iframeDoc).find(".modal-backdrop");
            if (backdrop.length) {
              backdrop.css({
                position: "fixed",
                top: "0px",
                left: "0px",
                right: "0px",
                bottom: "0px",
                height: "100vh",
              });
            }
          });

          // Clean up scroll listener and reset styles when modal closes
          $(iframeDoc).on("hidden.bs.modal", ".modal", function () {
            // Unpin the modal and restore parent scroll / inline styles.
            stopViewportPin(this);

            if (this.dataset.windowStickerTracked === "true") {
              const interaction = iframeWin.__lastWindowStickerInteraction;
              pushIframeAnalyticsEvent({
                event: "window_sticker_modal_close",
                page_location: window.location.href,
                page_path: window.location.pathname,
                click_element_id: interaction?.clickElementId || "",
                interaction_type: "window_sticker_modal",
                modal_id: this.getAttribute("id") || "",
                modal_target: interaction?.modalTarget || "",
                data_url: interaction?.dataUrl || "",
                analytics_source: "vhr_view_full_report",
              });
              delete this.dataset.windowStickerTracked;
            }
          });
        }
      } catch (e) {
        // Silently fail — modal still works, just might be off-screen
      }
    },
    [pushIframeAnalyticsEvent],
  );

  const pushWarrantyMileageEvent = useCallback(
    (eventData) => {
      pushIframeAnalyticsEvent(eventData);
    },
    [pushIframeAnalyticsEvent],
  );

  /**
   * Frontend-only fetch interception for sticker tracking
   * Captures all fetch calls from within the iframe (especially sticker requests)
   * WITHOUT requiring any backend modifications
   * This is a non-breaking transparent wrapper — original fetch still executes normally
   */
  const setupIframeFetchInterception = useCallback(
    (iframe) => {
      try {
        const iframeWindow = iframe.contentWindow;
        if (!iframeWindow || !iframeWindow.fetch) {
          return () => {};
        }

        const originalFetch = iframeWindow.fetch;
        const requestMap = new Map();

        const normalizeUrl = (url) => {
          if (!url) return "";
          try {
            return new URL(url, window.location.origin).toString();
          } catch (e) {
            return String(url);
          }
        };

        const isStickerRequestUrl = (requestUrl) => {
          if (!requestUrl) return false;
          return (
            requestUrl.includes("sticker") ||
            requestUrl.includes("window_sticker") ||
            requestUrl.includes("/api/sticker") ||
            /\.pdf(?:$|\?)/i.test(requestUrl)
          );
        };

        const resolveInteractionForRequest = (requestUrl) => {
          const interactions = iframeWindow.__windowStickerInteractions || {};
          const normalizedRequest = normalizeUrl(requestUrl);
          const keys = Object.keys(interactions);
          for (let i = keys.length - 1; i >= 0; i--) {
            const key = keys[i];
            const interaction = interactions[key];
            if (!interaction) continue;
            const interactionUrl = normalizeUrl(interaction.dataUrl);
            if (!interactionUrl) continue;
            if (
              normalizedRequest === interactionUrl ||
              normalizedRequest.startsWith(interactionUrl) ||
              interactionUrl.startsWith(normalizedRequest)
            ) {
              return interaction;
            }
          }
          return iframeWindow.__lastWindowStickerInteraction || null;
        };

        const clearUnobservableTimer = (correlationId) => {
          if (!correlationId) return;
          const timers = iframeWindow.__windowStickerOutcomeTimers;
          if (!timers || !timers[correlationId]) return;
          clearTimeout(timers[correlationId]);
          delete timers[correlationId];
        };

        const classifyHttpFailureType = (status) => {
          return status === 408 || status === 504 ? "timeout" : "http_error";
        };

        const classifyCaughtFailureType = (error) => {
          if (error?.name === "AbortError") {
            return "aborted";
          }
          const message = String(error?.message || "").toLowerCase();
          if (message.includes("timeout")) {
            return "timeout";
          }
          return "network_error";
        };

        const pushOutcomeEvent = (payload) => {
          const isSuccess = payload.outcome === "success";
          pushIframeAnalyticsEvent({
            event: isSuccess
              ? "Window Sticker - Load Success"
              : "Window Sticker - Load Failure",
            page_location: window.location.href,
            page_path: window.location.pathname,
            click_element_id: payload.clickElementId || "",
            interaction_type: payload.interactionType || "window_sticker_modal",
            flow_type: payload.flowType || "modal",
            data_url: payload.dataUrl || "",
            modal_target: payload.modalTarget || "",
            outcome: payload.outcome,
            error_type: payload.errorType || "",
            error_detail: payload.errorDetail || "",
            fetch_status: payload.fetchStatus,
            fetch_duration_ms: payload.duration,
            correlation_id: payload.correlationId || "",
            analytics_source: "vhr_view_full_report",
            fetch_method: "frontend_intercept",
          });
        };

        // Override fetch in iframe's context
        iframeWindow.fetch = function (...args) {
          const [resource] = args;
          const requestUrl =
            typeof resource === "string" ? resource : resource.url;

          // CRITICAL: Only track sticker-related requests to avoid noise
          const isStickerRequest = isStickerRequestUrl(requestUrl);

          if (!isStickerRequest) {
            // Not a sticker request — pass through unchanged
            return originalFetch.apply(this, args);
          }

          const interaction = resolveInteractionForRequest(requestUrl);
          const interactionType =
            interaction?.interactionType || "window_sticker_modal";
          const flowType =
            interactionType === "window_sticker_new_tab" ? "new_tab" : "modal";
          const clickElementId =
            interaction?.clickElementId || "windowStickerButton";
          const modalTarget = interaction?.modalTarget || "";
          const dataUrl = interaction?.dataUrl || requestUrl;
          const correlationId =
            interaction?.correlationId ||
            `sticker_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

          clearUnobservableTimer(correlationId);

          // STICKER REQUEST DETECTED — intercept and track
          requestMap.set(requestUrl, {
            correlationId,
            startTime: Date.now(),
            url: requestUrl,
            interactionType,
            flowType,
            clickElementId,
            modalTarget,
            dataUrl,
          });

          console.log(
            `[Sticker Fetch Intercept] Starting fetch for: ${requestUrl}`,
          );

          return originalFetch
            .apply(this, args)
            .then((response) => {
              const metadata = requestMap.get(requestUrl) || {};
              const duration = Date.now() - metadata.startTime;

              if (response.ok) {
                console.log(
                  `[Sticker Fetch Success] Status ${response.status} after ${duration}ms`,
                );

                pushOutcomeEvent({
                  outcome: "success",
                  clickElementId: metadata.clickElementId,
                  interactionType: metadata.interactionType,
                  flowType: metadata.flowType,
                  dataUrl: metadata.dataUrl,
                  modalTarget: metadata.modalTarget,
                  fetchStatus: response.status,
                  duration,
                  correlationId: metadata.correlationId,
                });

                return response;
              } else {
                const errorType = classifyHttpFailureType(response.status);
                console.warn(
                  `[Sticker Fetch HTTP Error] Status ${response.status} after ${duration}ms`,
                );

                pushOutcomeEvent({
                  outcome: "failure",
                  errorType,
                  clickElementId: metadata.clickElementId,
                  interactionType: metadata.interactionType,
                  flowType: metadata.flowType,
                  dataUrl: metadata.dataUrl,
                  modalTarget: metadata.modalTarget,
                  fetchStatus: response.status,
                  duration,
                  correlationId: metadata.correlationId,
                });

                return response;
              }
            })
            .catch((error) => {
              const metadata = requestMap.get(requestUrl) || {};
              const duration = Date.now() - metadata.startTime;
              const errorType = classifyCaughtFailureType(error);

              console.error(
                `[Sticker Fetch Error] ${error.message} after ${duration}ms`,
              );

              pushOutcomeEvent({
                outcome: errorType === "aborted" ? "aborted" : "failure",
                errorType,
                errorDetail: error?.message || "",
                clickElementId: metadata.clickElementId,
                interactionType: metadata.interactionType,
                flowType: metadata.flowType,
                dataUrl: metadata.dataUrl,
                modalTarget: metadata.modalTarget,
                fetchStatus: 0,
                duration,
                correlationId: metadata.correlationId,
              });

              throw error;
            })
            .finally(() => {
              const metadata = requestMap.get(requestUrl) || {};
              clearUnobservableTimer(metadata.correlationId);
              const interactions =
                iframeWindow.__windowStickerInteractions || {};
              if (
                metadata.correlationId &&
                interactions[metadata.correlationId]
              ) {
                interactions[metadata.correlationId].outcomeTracked = true;
              }
              requestMap.delete(requestUrl);
            });
        };

        console.log("Iframe fetch interception installed");

        // Cleanup function
        return () => {
          iframeWindow.fetch = originalFetch;
          requestMap.clear();
        };
      } catch (e) {
        return () => {};
      }
    },
    [pushIframeAnalyticsEvent],
  );

  const setupIframeWarrantyBridge = useCallback(
    (iframe) => {
      try {
        const iframeDoc = iframe.contentDocument;
        const iframeWin = iframe.contentWindow;
        if (!iframeDoc || !iframeWin) {
          return () => {};
        }

        const parseLimitValue = (raw) => {
          const value = String(raw == null ? "" : raw).trim();
          if (!value || value === "-" || /^N\/?A$/i.test(value)) {
            return { kind: "na" };
          }
          if (/^(UNLIMITED|LIFETIME)$/i.test(value)) {
            return { kind: "unlimited" };
          }
          const digits = value.replace(/,/g, "");
          if (/^-?\d+$/.test(digits)) {
            return { kind: "number", value: parseInt(digits, 10) };
          }
          return { kind: "na" };
        };

        const setCoverageState = (input, messageEl, isValid, msg) => {
          input.classList.toggle("is-invalid", !isValid);
          input.classList.toggle("is-valid", isValid);
          input.setAttribute("aria-invalid", isValid ? "false" : "true");
          if (!messageEl) return;
          if (!msg) {
            messageEl.textContent = "";
            messageEl.classList.add("d-none");
            messageEl.classList.remove("text-danger", "text-success");
            return;
          }
          messageEl.innerHTML = isValid
            ? "<i class='fa fa-check-circle' aria-hidden='true'></i> " + msg
            : msg;
          messageEl.classList.remove("d-none", "text-danger", "text-success");
          messageEl.classList.add(isValid ? "text-success" : "text-danger");
        };

        const buildStatusIconHtml = (status, iconConfig) => {
          if (status === "ACTIVE") {
            return (
              "<span class='warranty-status-icon active' role='img' title='" +
              iconConfig.activeTitle +
              "' aria-label='" +
              iconConfig.activeAria +
              "'><img src='" +
              iconConfig.successSrc +
              "' alt='" +
              iconConfig.activeTitle +
              "' width='22px'/></span>"
            );
          }
          if (status === "EXPIRED") {
            return (
              "<span class='warranty-status-icon expired' role='img' title='" +
              iconConfig.expiredTitle +
              "' aria-label='" +
              iconConfig.expiredAria +
              "'><img src='" +
              iconConfig.expiredSrc +
              "' alt='" +
              iconConfig.expiredTitle +
              "' width='20px'/></span>"
            );
          }
          return status ? "<span>" + status + "</span>" : "<span>-</span>";
        };

        const formatMonths = (months, labels) => {
          if (months < 0) return "-";
          return months === 1
            ? "1 " + labels.monthLabel
            : months + " " + labels.monthsLabel;
        };

        const getRowCoverageResult = (
          milesLimit,
          monthsLimit,
          currentMileage,
          currentAge,
          labels,
        ) => {
          let status = "N/A";
          let cls = "text-muted";
          let remMi = "N/A";
          let remTm = "N/A";

          if (milesLimit.kind === "na" && monthsLimit.kind === "na") {
            return { status, cls, remMi, remTm };
          }

          const hasMi = milesLimit.kind === "number";
          const hasAge = monthsLimit.kind === "number" && currentAge != null;
          const remMiles = hasMi ? milesLimit.value - currentMileage : null;
          const miExp = hasMi && remMiles <= 0;
          const ageExp = hasAge && currentAge >= monthsLimit.value;

          if (
            milesLimit.kind === "unlimited" &&
            monthsLimit.kind === "unlimited"
          ) {
            status = "ACTIVE";
            cls = "text-success";
            remMi = labels.unlimitedMilesLeftLabel;
            remTm = labels.lifetimeLabel;
            return { status, cls, remMi, remTm };
          }

          if (miExp || ageExp) {
            status = "EXPIRED";
            cls = "text-danger";
            remMi =
              miExp || !hasMi ? "-" : Number(remMiles).toLocaleString("en-US");
            remTm = "-";
            return { status, cls, remMi, remTm };
          }

          const actMi = !hasMi || remMiles > 0;
          const actAge = !hasAge || currentAge <= monthsLimit.value;
          if (actMi && actAge) {
            status = "ACTIVE";
            cls = "text-success";
            remMi =
              milesLimit.kind === "unlimited"
                ? labels.unlimitedMilesLeftLabel
                : hasMi
                  ? Number(remMiles).toLocaleString("en-US")
                  : "N/A";
            remTm =
              monthsLimit.kind === "unlimited"
                ? labels.lifetimeLabel
                : hasAge
                  ? formatMonths(
                      Math.max(Math.floor(monthsLimit.value - currentAge), 0),
                      labels,
                    )
                  : "N/A";
            return { status, cls, remMi, remTm };
          }

          status = "EXPIRED";
          cls = "text-danger";
          remMi = "-";
          remTm = "-";
          return { status, cls, remMi, remTm };
        };

        const resolveWindowStickerButton = (clickedEl) => {
          if (!clickedEl) {
            return null;
          }
          // Use lowercase for case-insensitive match (button text may be
          // capitalised by applyWindowStickerButtonRules).
          const text = (clickedEl.textContent || "").toLowerCase();
          return (
            clickedEl.closest(
              ".open-sticker-modal, button[data-url], a[data-url]",
            ) ||
            (text.includes("view window sticker") ||
            text.includes("view vehicle label") ||
            text.includes("window sticker")
              ? clickedEl.closest("button, a")
              : null)
          );
        };

        const getWindowStickerModalTarget = (buttonEl, iframeDoc) => {
          if (!buttonEl) {
            return "";
          }

          const href = buttonEl.getAttribute("href") || "";
          const className = buttonEl.className || "";

          // Report-service sticker button uses custom modal implementation.
          // If it has sticker modal semantics but no bootstrap target attributes,
          // infer stickerModal as the modal target so flow_type remains correct.
          const looksLikeStickerButton =
            className.includes("open-sticker-modal") ||
            buttonEl.hasAttribute("data-url") ||
            /window\s*sticker/i.test(buttonEl.textContent || "");
          const hasStickerModal = !!iframeDoc?.getElementById("stickerModal");

          const explicitTarget =
            buttonEl.getAttribute("data-target") ||
            buttonEl.getAttribute("data-bs-target") ||
            (href.startsWith("#") ? href : "");

          if (explicitTarget) {
            return explicitTarget;
          }

          return looksLikeStickerButton && hasStickerModal
            ? "#stickerModal"
            : "";
        };

        const resolveWindowStickerDownloadElement = (clickedEl) => {
          if (!clickedEl) {
            return null;
          }

          const direct = clickedEl.closest(
            "#downloadWindowStickerBtn, #downloadBtn, a[download], .download-link",
          );
          if (direct) {
            return direct;
          }

          const candidate = clickedEl.closest("button, a");
          if (!candidate) {
            return null;
          }

          const text = (candidate.textContent || "").toLowerCase();
          if (text.includes("download") && text.includes("sticker")) {
            return candidate;
          }

          return null;
        };

        const handleWindowStickerClick = (event) => {
          const clickedEl = event.target;

          const downloadElement =
            resolveWindowStickerDownloadElement(clickedEl);
          if (downloadElement) {
            const interaction = iframeWin.__lastWindowStickerInteraction || {};
            const clickElementId =
              downloadElement.getAttribute("id") ||
              interaction.clickElementId ||
              "windowStickerDownloadButton";

            pushIframeAnalyticsEvent({
              event: "Window Sticker - Download Click",
              page_location: window.location.href,
              page_path: window.location.pathname,
              click_element_id: clickElementId,
              interaction_type:
                interaction.interactionType || "window_sticker_modal",
              flow_type:
                interaction.interactionType === "window_sticker_new_tab"
                  ? "new_tab"
                  : "modal",
              modal_target: interaction.modalTarget || "",
              data_url:
                interaction.dataUrl ||
                downloadElement.getAttribute("href") ||
                "",
              correlation_id: interaction.correlationId || "",
              analytics_source: "vhr_view_full_report",
            });
            return;
          }

          const windowStickerButton = resolveWindowStickerButton(clickedEl);
          if (!windowStickerButton) {
            return;
          }

          const modalTarget = getWindowStickerModalTarget(
            windowStickerButton,
            iframeDoc,
          );

          const dataUrl =
            windowStickerButton.getAttribute("data-url") ||
            windowStickerButton.getAttribute("href") ||
            "";
          const clickElementId =
            windowStickerButton.getAttribute("id") || "windowStickerButton";

          // Rule-driven interaction type overrides whatever the button attributes say.
          const interactionType = modalTarget
            ? "window_sticker_modal"
            : "window_sticker_new_tab";

          const correlationId = `sticker_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

          const interactions = iframeWin.__windowStickerInteractions || {};
          const outcomeTimers = iframeWin.__windowStickerOutcomeTimers || {};

          iframeWin.__lastWindowStickerInteraction = {
            clickElementId,
            dataUrl,
            modalTarget,
            interactionType,
            correlationId,
            outcomeTracked: false,
            timestamp: Date.now(),
          };
          interactions[correlationId] =
            iframeWin.__lastWindowStickerInteraction;
          iframeWin.__windowStickerInteractions = interactions;
          iframeWin.__windowStickerOutcomeTimers = outcomeTimers;

          if (interactionType === "window_sticker_new_tab") {
            outcomeTimers[correlationId] = setTimeout(() => {
              const latestInteractions =
                iframeWin.__windowStickerInteractions || {};
              const item = latestInteractions[correlationId];
              if (!item || item.outcomeTracked) {
                return;
              }

              pushIframeAnalyticsEvent({
                event: "Window Sticker - Load Failure",
                page_location: window.location.href,
                page_path: window.location.pathname,
                click_element_id: item.clickElementId || "",
                interaction_type:
                  item.interactionType || "window_sticker_new_tab",
                flow_type: "new_tab",
                modal_target: item.modalTarget || "",
                data_url: item.dataUrl || "",
                outcome: "unobservable_detached_tab",
                error_type: "detached_tab",
                correlation_id: item.correlationId || "",
                analytics_source: "vhr_view_full_report",
                fetch_method: "frontend_intercept",
              });
              item.outcomeTracked = true;
              delete outcomeTimers[correlationId];
            }, 1500);
          }

          pushIframeAnalyticsEvent({
            event: "window_sticker_click",
            page_location: window.location.href,
            page_path: window.location.pathname,
            click_element_id: clickElementId,
            interaction_type: interactionType,
            flow_type:
              interactionType === "window_sticker_new_tab"
                ? "new_tab"
                : "modal",
            modal_target: modalTarget,
            data_url: dataUrl,
            correlation_id: correlationId,
            analytics_source: "vhr_view_full_report",
          });
        };

        iframeWin.handleCoverageButtonClick = function (event) {
          const target = event && event.target ? event.target : null;
          const section = target
            ? target.closest("#warranty")
            : iframeDoc.getElementById("warranty");
          if (!section) {
            return;
          }
          const sectionData = section.dataset || {};
          let clickElement = section.querySelector("#coverageBtn");
          if (!clickElement && target) {
            clickElement = target.closest("button[id], a[id]");
          }
          const clickElementId =
            (clickElement && clickElement.getAttribute("id")) || "coverageBtn";
          const input = section.querySelector("#coverage");
          const messageEl = section.querySelector("#coverageMessage");
          const preloader = section.querySelector("#warrantyPreloader");
          if (!input) {
            return;
          }
          if (preloader) preloader.classList.remove("d-none");
          const hidePreloader = function () {
            if (preloader)
              setTimeout(function () {
                preloader.classList.add("d-none");
              }, 120);
          };
          const normalized = (input.value || "").replace(/,/g, "").trim();

          const trackWarrantyMileageEvent = function (
            validationStatus,
            reason,
            coverageSummary,
          ) {
            const eventData = {
              event: clickElementId,
              page_location: window.location.href,
              page_path: window.location.pathname,
              click_element_id: clickElementId,
              interaction_type: "mileage_submit",
              validation_status: validationStatus,
              validation_reason: reason || "",
              entered_mileage: normalized || "",
              analytics_source: "vhr_view_full_report",
            };
            if (coverageSummary) {
              eventData.active_coverage_count = coverageSummary.active;
              eventData.expired_coverage_count = coverageSummary.expired;
              eventData.na_coverage_count = coverageSummary.na;
            }
            pushWarrantyMileageEvent(eventData);
          };

          const msgEnter = sectionData.msgEnterMileage || "Enter Mileage";
          const msgValid =
            sectionData.msgValidMileage ||
            "Enter a valid non-negative mileage.";
          const msgGood = sectionData.msgGood || "Looks good!";

          if (!normalized) {
            setCoverageState(input, messageEl, false, msgEnter);
            trackWarrantyMileageEvent("invalid", "empty_mileage");
            input.focus();
            hidePreloader();
            return;
          }
          if (!/^\d+$/.test(normalized)) {
            setCoverageState(input, messageEl, false, msgValid);
            trackWarrantyMileageEvent("invalid", "non_numeric_mileage");
            input.focus();
            hidePreloader();
            return;
          }

          setCoverageState(input, messageEl, true, msgGood);
          const table = section.querySelector("#warrantyDetail");
          if (!table) {
            trackWarrantyMileageEvent("invalid", "missing_warranty_table");
            hidePreloader();
            return;
          }

          const currentMileage = parseInt(normalized, 10);
          const parsedAge = parseLimitValue(
            table.getAttribute("data-warranty-age"),
          );
          const currentAge =
            parsedAge.kind === "number" ? parsedAge.value : null;

          const successSrc =
            table.getAttribute("data-icon-success") ||
            "../fullReport/img/icon-success-pass.png";
          const expiredSrc =
            table.getAttribute("data-icon-expired") ||
            "../fullReport/img/icon-expired.png";
          const iconConfig = {
            successSrc,
            expiredSrc,
            activeTitle: sectionData.msgStatusActive || "Active",
            expiredTitle: sectionData.msgStatusExpired || "Expired",
            activeAria: sectionData.msgCoverageActive || "Coverage active",
            expiredAria: sectionData.msgCoverageExpired || "Coverage expired",
          };
          const labels = {
            monthLabel: sectionData.msgMonth || "month",
            monthsLabel: sectionData.msgMonths || "months",
            milesLeftLabel: sectionData.msgMilesLeft || "miles left",
            lifetimeLabel: sectionData.msgLifetime || "Lifetime",
          };
          labels.unlimitedMilesLeftLabel =
            sectionData.msgUnlimitedMilesLeft ||
            "Unlimited " + labels.milesLeftLabel;

          const rows = table.querySelectorAll("tbody tr");
          let activeCount = 0;
          let expiredCount = 0;
          let naCount = 0;

          for (let i = 0; i < rows.length; i++) {
            const cells = rows[i].querySelectorAll("td");
            if (cells.length < 5) continue;
            const milesLimit = parseLimitValue(
              rows[i].getAttribute("data-warranty-miles"),
            );
            const monthsLimit = parseLimitValue(
              rows[i].getAttribute("data-warranty-months"),
            );
            const { status, cls, remMi, remTm } = getRowCoverageResult(
              milesLimit,
              monthsLimit,
              currentMileage,
              currentAge,
              labels,
            );

            cells[1].textContent = remMi;
            cells[2].textContent = remTm;
            cells[3].classList.remove(
              "text-danger",
              "text-success",
              "text-muted",
            );
            cells[3].classList.add(cls);
            cells[3].setAttribute("data-status", status);
            cells[3].innerHTML = buildStatusIconHtml(status, iconConfig);

            if (status === "ACTIVE") activeCount += 1;
            else if (status === "EXPIRED") expiredCount += 1;
            else naCount += 1;
          }

          trackWarrantyMileageEvent("valid", "coverage_calculated", {
            active: activeCount,
            expired: expiredCount,
            na: naCount,
          });

          hidePreloader();
        };

        iframeDoc.addEventListener("click", handleWindowStickerClick, true);

        return () => {
          iframeDoc.removeEventListener(
            "click",
            handleWindowStickerClick,
            true,
          );
          // Some window properties can be non-configurable in certain runtimes.
          // Only clear references; avoid delete on window properties.
          try {
            iframeWin.handleCoverageButtonClick = undefined;
          } catch (e) {
            // no-op
          }
          try {
            iframeWin.__lastWindowStickerInteraction = undefined;
          } catch (e) {
            // no-op
          }
          const timers = iframeWin.__windowStickerOutcomeTimers || {};
          Object.keys(timers).forEach((key) => clearTimeout(timers[key]));
          try {
            iframeWin.__windowStickerOutcomeTimers = undefined;
            iframeWin.__windowStickerInteractions = undefined;
          } catch (e) {
            // no-op
          }
        };
      } catch (e) {
        return () => {};
      }
    },
    [pushIframeAnalyticsEvent, pushWarrantyMileageEvent],
  );

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !reportHtml) return;

    let observer = null;
    let resizeHandler = null;
    let cleanupWarrantyBridge = null;
    let cleanupFetchInterception = null;
    // While a modal overlay is active the iframe is forced to full-viewport
    // height; don't override it with the content height.
    if (iframe.__modalOverlayActive) return;
    const resizeIframe = () => {
      try {
        // While a modal overlay is active the iframe is forced to full-viewport
        // height; don't override it with the content height.
        if (iframe.__modalOverlayActive) return;
        if (iframe.contentDocument && iframe.contentDocument.body) {
          const contentHeight =
            iframe.contentDocument.documentElement.scrollHeight;
          if (contentHeight > 0) {
            iframe.style.height = contentHeight + "px";
          }
        }
      } catch (e) {
        // Silently fail if access is restricted
      }
    };

    const handleLoad = () => {
      resizeIframe();

      // Apply the modal positioning fix after iframe is fully loaded
      applyIframeModalFix(iframe);

      // Setup warranty bridge for mileage validation tracking
      cleanupWarrantyBridge = setupIframeWarrantyBridge(iframe);

      // Setup fetch interception for sticker fetch success/failure tracking (frontend-only, no backend changes)
      cleanupFetchInterception = setupIframeFetchInterception(iframe);

      try {
        if (iframe.contentDocument && iframe.contentDocument.body) {
          observer = new MutationObserver(resizeIframe);
          observer.observe(iframe.contentDocument.body, {
            childList: true,
            subtree: true,
            attributes: true,
          });
          resizeHandler = resizeIframe;
          iframe.contentWindow.addEventListener("resize", resizeHandler);
        }
      } catch (e) {
        // Silently fail
      }
    };

    iframe.addEventListener("load", handleLoad);

    return () => {
      iframe.removeEventListener("load", handleLoad);
      if (cleanupWarrantyBridge) {
        cleanupWarrantyBridge();
      }
      if (cleanupFetchInterception) {
        cleanupFetchInterception();
      }
      if (iframe.__stickerModalObserver) {
        iframe.__stickerModalObserver.disconnect();
        delete iframe.__stickerModalObserver;
      }
      // Safety net: release any lingering scroll lock if the report is
      // unmounted (e.g. navigation) while a modal is still open.
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      if (observer) {
        observer.disconnect();
      }
      try {
        if (resizeHandler && iframe.contentWindow) {
          iframe.contentWindow.removeEventListener("resize", resizeHandler);
        }
      } catch (e) {
        // Silently fail
      }
    };
  }, [
    reportHtml,
    applyIframeModalFix,
    setupIframeWarrantyBridge,
    setupIframeFetchInterception,
  ]);

  function downloadPDF(base64Data) {
    const arrBuffer = base64ToArrayBuffer(base64Data);
    const newBlob = new Blob([arrBuffer], { type: "application/pdf" });

    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(newBlob);
      return;
    }

    const data = window.URL.createObjectURL(newBlob);
    const link = document.createElement("a");
    document.body.appendChild(link);
    link.href = data;
    link.title = "Print Autocheck Report";
    link.target = "_blank";
    link.click();

    setTimeout(() => {
      window.URL.revokeObjectURL(data);
    }, 10000);
    link.remove();
  }

  function base64ToArrayBuffer(data) {
    const binaryString = window.atob(data);
    const binaryLen = binaryString.length;
    const bytes = new Uint8Array(binaryLen);
    for (let i = 0; i < binaryLen; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  const getPDFReport = async () => {
    const result = await dispatch(
      fetchReports({
        condition: "getFullReport",
        payload: { vin, isOwner, fileType: "PDF" },
      }),
    ).unwrap();
    localStorage.removeItem("vhr.vehicle");
    return result?.response?.data[0]?.pdfFull;
  };

  const printPdfFullReport = async () => {
    const pdfBase64 = await getPDFReport();
    if (pdfBase64) {
      downloadPDF(pdfBase64);
    }
  };

  return (
    <>
      <div className="container navMarginMobile mydashboard">
        <div className="row mt-4 fullReport">
          <div className="col-12 mt-4 mb-4">
            <div className="d-flex gap-2 mb-4">
              <button
                type="button"
                onClick={printPdfFullReport}
                className="btn btn-primary base-button buttonPrimary"
              >
                <span>Print Report</span>
              </button>
              <button
                type="button"
                className="btn btn-primary base-button buttonPrimary"
                onClick={handleGoDashboard}
              >
                <span>Run More Reports</span>
              </button>
            </div>
            <Link
              to="/vehiclehistory/dashboard"
              onClick={handleGoDashboard}
              className="linkstyle"
            >
              Return to my VHR Dashboard »
            </Link>
          </div>
        </div>

        {!reportHtml && !viewFullReport[0]?.html ? (
          <div>Loading report...</div>
        ) : (
          <iframe
            id="FileFrame"
            ref={iframeRef}
            srcDoc={reportHtml}
            title="Vehicle History Report"
            // scrolling="yes"

            // frameBorder="0"
            style={{
              overflow: "visible",
              minHeight: "100vh",
              width: "100%",
              border: "none",
            }}
          />
        )}
        {vinnyData.vinnyHtml && (
          <VinnyPrompt
            htmlString={`<div id="vinny-container">${vinnyData.vinnyHtml}</div>`}
            scripts={vinnyData.vinnyScripts}
            stylesheets={vinnyData.vinnyStylesheets}
          />
        )}
      </div>
    </>
  );
};

export default VHR_viewFullReports;
